import { Link } from "@tanstack/react-router"
import { ExternalLink, Pencil, User } from "lucide-react"
import type { ReactNode } from "react"
import { PageContainer } from "../layout/PageContainer"
import { AnimatedTabs } from "../ui/AnimatedTabs"

/** Props for owner variant (editable, own profile) */
interface OwnerHeaderProps {
	variant: "owner"
	displayName: string
	avatarUrl: string | null
}

/** Props for public variant (view-only, other user's profile) */
interface PublicHeaderProps {
	variant: "public"
	displayName: string
	avatarUrl: string
	explorerUrl: string | null
}

type ProfileHeaderProps = {
	/** Stats section content */
	stats: ReactNode
	/** Active tab */
	activeTab: "positions" | "activity"
	/** Tab change handler */
	onTabChange: (tab: "positions" | "activity") => void
} & (OwnerHeaderProps | PublicHeaderProps)

const TABS: Array<{ id: "positions" | "activity"; label: string }> = [
	{ id: "positions", label: "Positions" },
	{ id: "activity", label: "Activity" },
]

/**
 * Shared profile header component for both owner and public profile pages.
 * Handles avatar display, name, action buttons, and tabs.
 */
export function ProfileHeader(props: ProfileHeaderProps) {
	const { stats, activeTab, onTabChange } = props

	return (
		<div className="border-border-subtle border-b bg-bg-card">
			<PageContainer className="py-4 sm:py-6">
				{/* Avatar and Name Row */}
				<div className="mb-4 flex items-center gap-3 sm:mb-6 sm:gap-4">
					{props.variant === "owner" ? (
						<OwnerAvatar avatarUrl={props.avatarUrl} />
					) : (
						<PublicAvatar avatarUrl={props.avatarUrl} displayName={props.displayName} />
					)}

					<div className="min-w-0 flex-1">
						<div className="flex items-center gap-2">
							<p className="truncate font-display font-semibold text-lg text-text-primary sm:text-2xl">
								{props.displayName}
							</p>
							{props.variant === "owner" ? (
								<EditButton />
							) : props.explorerUrl ? (
								<ExplorerButton href={props.explorerUrl} />
							) : null}
						</div>
					</div>
				</div>

				{/* Stats Grid - Card Layout */}
				<div className="grid grid-cols-3 gap-2 sm:gap-4">{stats}</div>
			</PageContainer>

			{/* Main Tabs */}
			<PageContainer className="border-border-subtle border-b">
				<AnimatedTabs activeTab={activeTab} onTabChange={onTabChange} tabs={TABS} />
			</PageContainer>
		</div>
	)
}

/** Avatar for owner profile (editable with hover effect) */
function OwnerAvatar({ avatarUrl }: { avatarUrl: string | null }) {
	return (
		<Link aria-label="Edit profile picture" className="group relative shrink-0" to="/profile/edit">
			<div className="flex size-14 items-center justify-center overflow-hidden rounded-full bg-bg-tertiary sm:size-16">
				{avatarUrl ? (
					<img
						alt="Avatar"
						className="size-full object-cover"
						decoding="async"
						height={64}
						loading="lazy"
						referrerPolicy="no-referrer"
						src={avatarUrl}
						width={64}
					/>
				) : (
					<User aria-hidden="true" className="size-6 text-text-muted sm:size-7" />
				)}
			</div>
			<div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
				<Pencil aria-hidden="true" className="size-4 text-white" />
			</div>
		</Link>
	)
}

/** Avatar for public profile (non-editable) */
function PublicAvatar({ avatarUrl, displayName }: { avatarUrl: string; displayName: string }) {
	return (
		<div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-bg-tertiary sm:size-16">
			<img
				alt={`${displayName}'s avatar`}
				className="size-full object-cover"
				decoding="async"
				height={64}
				loading="lazy"
				referrerPolicy="no-referrer"
				src={avatarUrl}
				width={64}
			/>
		</div>
	)
}

/** Edit button for owner profile */
function EditButton() {
	return (
		<Link
			aria-label="Edit profile"
			className="flex size-8 items-center justify-center rounded-full transition-colors hover:bg-bg-tertiary"
			to="/profile/edit"
		>
			<Pencil aria-hidden="true" className="size-3.5 text-text-muted" />
		</Link>
	)
}

/** External link button for public profile */
function ExplorerButton({ href }: { href: string }) {
	return (
		<a
			aria-label="View on block explorer"
			className="flex size-8 items-center justify-center rounded-full text-text-muted transition-colors hover:bg-bg-tertiary hover:text-text-primary"
			href={href}
			rel="noopener noreferrer"
			target="_blank"
		>
			<ExternalLink aria-hidden="true" className="size-3.5" />
		</a>
	)
}

interface ProfileStatProps {
	/** The stat value (number or formatted string) */
	value: string
	/** Label for the stat */
	label: string
	/** Icon element to display in the badge */
	icon: ReactNode
	/** Icon badge background color class */
	iconBgClass?: string
	/** Optional actions (buttons) below the value */
	actions?: ReactNode
	/** Additional className for the value */
	valueClassName?: string
	/** Whether this card should have highlight styling (e.g., for claimable) */
	highlight?: boolean
}

/**
 * Individual stat card component for profile headers.
 * Premium card-based design with gradient backgrounds and icon badges.
 */
export function ProfileStat({
	value,
	label,
	icon,
	iconBgClass = "bg-text-muted/10",
	actions,
	valueClassName,
	highlight = false,
}: ProfileStatProps) {
	return (
		<div
			className={`relative overflow-hidden rounded-2xl border p-3 sm:p-5 ${
				highlight
					? "border-breakout-brand/30 bg-gradient-to-br from-breakout-brand/5 to-breakout-brand/10"
					: "border-border-subtle bg-gradient-to-br from-bg-secondary to-bg-card dark:border-white/10 dark:from-white/[0.04] dark:to-white/[0.02]"
			}`}
		>
			{/* Subtle glow effect when highlighted */}
			{highlight && (
				<div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-breakout-brand/5 via-transparent to-transparent" />
			)}

			{/* Icon + Label row */}
			<div className="relative mb-1 flex items-center gap-1.5 sm:mb-2">
				<div
					className={`flex size-5 items-center justify-center rounded-full sm:size-6 ${iconBgClass}`}
				>
					{icon}
				</div>
				<span className="font-medium text-text-secondary text-xs sm:text-sm">{label}</span>
			</div>

			{/* Value */}
			<p
				className={`relative font-bold text-xl tabular-nums sm:text-3xl ${valueClassName ?? "text-text-primary"}`}
			>
				{value}
			</p>

			{/* Actions area - always reserve space to prevent layout shift */}
			<div className="relative mt-3 min-h-[32px] sm:min-h-[40px]">{actions}</div>
		</div>
	)
}
